// @ts-strict-ignore
import {
  Component,
  ViewEncapsulation,
  ViewChild,
  ElementRef,
  AfterViewInit,
  ViewContainerRef,
  ComponentRef,
} from '@angular/core';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { interval, timer } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UI_STRINGS } from '../../../../functions/language/uistrings';
import { Language } from '../../../../model/enums/language';
import { LanguageService } from '../../../../services/language.service';
import { InputSelectComponent } from '../input-select/input-select.component';
import { ExtraComponentList } from './information-dialog-data';
import { InformationDialogType } from './information-dialog-type';

@Component({
    selector: 'app-information-dialog',
    templateUrl: './information-dialog.component.html',
    styleUrls: ['./information-dialog.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class InformationDialogComponent implements AfterViewInit {
  @ViewChild('acceptbutton', { read: ElementRef }) private acceptButton: ElementRef;
  @ViewChild('extracomponent', { read: ViewContainerRef }) extraComponent: ViewContainerRef;

  countDown: number;

  private extraComponentRef: ComponentRef<any>;

  readonly GLOBAL_STRINGS = UI_STRINGS.GLOBAL;

  get acceptButtonText(): string {
    return this.config.data.acceptLabel;
  }

  get dialogTitle(): string {
    return this.config.data.title;
  }

  get dialogTypeClass(): string {
    switch (this.config.data.type) {
      case InformationDialogType.Warning:
        return 'warning-dialog';
      case InformationDialogType.Error:
        return 'error-dialog';
      default:
        return 'information-dialog';
    }
  }

  get hasDialogTitle(): boolean {
    return this.config.data?.title;
  }

  get hasExtraInformation(): boolean {
    return !!this.config.data?.extraInformation;
  }

  get id(): string {
    return this.config.data?.id;
  }

  get isEnglishTitle(): boolean {
    return this._languageService.language === Language.ENGLISH;
  }

  get preserveTitleFormatting(): boolean {
    return this.config.data.preserveTitleFormatting;
  }

  get rejectButtonText(): string {
    return this.config.data.rejectLabel;
  }

  get role(): string {
    return this.config.data?.role;
  }

  get supportLineBreaks(): boolean {
    return this.config.data.lineBreaks;
  }

  constructor(
    public config: DynamicDialogConfig,
    public ref: DynamicDialogRef,
    private _hostElementRef: ElementRef,
    private _languageService: LanguageService
  ) {
    if (config.data?.timeLimit) {
      this._initCountDown(config.data.timeLimit);
    }
  }

  ngAfterViewInit(): void {
    this._focusAcceptButtonOnLoad();
    this._overridePrimengDialogRole();
    this._loadExtraComponent();
  }

  accept() {
    const instanceRef = this.extraComponentRef?.instance;
    if (instanceRef instanceof InputSelectComponent) {
      this.ref.close({ data: instanceRef.model.model.optionValue });
    } else {
      this.ref.close(true);
    }
  }

  onTertiaryActionClick(): void {
    this.config.data.tertiaryAction();
  }

  reject() {
    this.ref.close(false);
  }

  private _focusAcceptButtonOnLoad(): void {
    setTimeout(() => this.acceptButton.nativeElement.firstChild.focus());
  }

  private _initCountDown(timeLimit: number): void {
    this.countDown = timeLimit;
    const source = interval(1000);
    const timer$ = timer(11000);
    const reload = source.pipe(takeUntil(timer$));
    reload.subscribe((val: number) => {
      this.countDown = 10 - (val + 1);
      if (this.countDown <= 0) {
        this.accept();
      }
    });
  }

  private async _loadExtraComponent() {
    const extraComponentData = this.config.data?.extraComponent;
    if (extraComponentData) {
      this.extraComponent.clear();
      const componentInstance = await ExtraComponentList.find((ec) => ec.name === extraComponentData.name).component();
      this.extraComponentRef = this.extraComponent.createComponent(componentInstance);
      if (extraComponentData.inputData) {
        for (const [key, value] of Object.entries(extraComponentData.inputData)) {
          this.extraComponentRef.setInput(key, value);
        }
      }
    }
  }

  private _overridePrimengDialogRole(): void {
    const role = this.config.data?.role;
    const pDialogDiv: HTMLDivElement = this._hostElementRef.nativeElement?.closest('[role="dialog"]');

    if (!!role && !!pDialogDiv) {
      pDialogDiv.setAttribute('role', role);
      pDialogDiv.setAttribute('aria-labelledby', role + 'heading');
    }
  }
}
